import { Button } from "@dropbox/dig-components/buttons";
import { Text } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import { PAP_Select_DashSearchResultSeeMore } from "@mirage/analytics/events/types/select_dash_search_results_see_more";
import { useResultPAPLogger } from "@mirage/shared/hooks/useResultPAPLogger";
import {
  concatActiveFilterBindings,
  contentTypeToSearchFilter,
} from "@mirage/shared/search/search-filters";
import i18n from "@mirage/translations";
import { reportEvent } from "@mirage/webapp/analytics/logging";
import { useCallback, useEffect, useState } from "react";
import { CategoryImageGrid } from "./CategoryImageGrid";
import { CategoryResultList } from "./CategoryResultList";
import styles from "./CategorySearchAccordion.module.css";
import { useCategorySearch } from "./useCategorySearch";

import type { SearchResult } from "@mirage/service-dbx-api";
import type { SearchFilter } from "@mirage/shared/search/search-filters";
import type { CategoryConfig } from "./config";

type Props = {
  query: string;
  seeAllForCategory: (filter: SearchFilter[]) => void;
  categories: CategoryConfig[];
  hasResults?: () => void;
  shouldSkipCache?: boolean;
};

export function CategorySearchAccordion({
  query,
  seeAllForCategory,
  categories,
  hasResults,
  shouldSkipCache,
}: Props) {
  const [categoryHasResults, setCategoryHasResults] = useState<
    Record<string, boolean>
  >({});

  const categoryResultsReturned = (categoryId: string) => {
    setCategoryHasResults((prev) => ({
      ...prev,
      [categoryId]: true,
    }));
    hasResults?.();
  };

  useEffect(() => {
    if (query) {
      setCategoryHasResults({});
    }
  }, [query]);

  return (
    <Box marginBottom="24" width="100%">
      {categories.map((category) => {
        return (
          <Category
            key={category.id}
            query={query}
            category={category}
            hasResults={() => categoryResultsReturned(category.id)}
            seeAllForCategory={seeAllForCategory}
            shouldSkipCache={shouldSkipCache}
            categoryHasResults={categoryHasResults[category.id]}
          />
        );
      })}
    </Box>
  );
}

type CategoryProps = {
  query: string;
  category: CategoryConfig;
  seeAllForCategory: (filter: SearchFilter[]) => void;
  hasResults?: () => void;
  shouldSkipCache?: boolean;
  categoryHasResults?: boolean;
};

function Category({
  query,
  category,
  hasResults,
  seeAllForCategory,
  shouldSkipCache,
  categoryHasResults,
}: CategoryProps) {
  const { loading, results } = useCategorySearch(
    category,
    query,
    shouldSkipCache,
  );
  const { logSearchResultOpen, logSearchResultShown } = useResultPAPLogger();

  useEffect(() => {
    if (category.id !== "images" && results.length > 0) {
      hasResults?.();
    }
  }, [results]);

  const onImageLoadSuccess = () => {
    hasResults?.();
  };

  const handleResultClick = useCallback(
    (result: SearchResult) => {
      const filter = contentTypeToSearchFilter(category.contentType);
      logSearchResultOpen({
        defaultProps: {
          query,
          searchResult: result,
          filters: filter ? [filter] : [],
          results,
          featureLine: "search",
        },
        rightRailOpened: false,
        isTypeahead: false,
        launchMethod: "click",
        actionSurfaceComponent: "result_row",
        categorizedResult: true,
      });
    },
    [logSearchResultOpen, query, category.contentType, results],
  );

  const handleResultShown = useCallback(
    (result: SearchResult) => {
      const filter = contentTypeToSearchFilter(category.contentType);
      logSearchResultShown(
        {
          query,
          searchResult: result,
          filters: filter ? [filter] : [],
          results,
          featureLine: "search",
        },
        false,
        "serp_categories",
      );
    },
    [logSearchResultShown, query, category.contentType, results],
  );

  const handleSeeAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    const filter = contentTypeToSearchFilter(category.contentType);
    if (!filter) return;
    seeAllForCategory([filter]);
    reportEvent(
      PAP_Select_DashSearchResultSeeMore({
        featureLine: "search",
        categorizedResult: true,
        activeFilters: concatActiveFilterBindings([filter]),
      }),
    );
  };

  return (
    <Box
      className={categoryHasResults ? styles.showCategory : styles.hideCategory}
      display="flex"
      flexDirection="column"
      key={category.id}
    >
      <div className={styles.accordionHeader}>
        <Box
          width="100%"
          display="flex"
          alignItems="center"
          borderWidth="1"
          justifyContent="space-between"
          paddingBottom="16"
        >
          <Text variant="label" size="medium" color="subtle">
            {category.label}
          </Text>
          <Box display="flex" alignItems="center" className={styles.gapSpacing}>
            <Button size="small" variant="transparent" onClick={handleSeeAll}>
              {i18n.t("see_all")}
            </Button>
          </Box>
        </Box>
      </div>
      {category.layout === "list" ? (
        <CategoryResultList
          results={results}
          query={query}
          loading={loading}
          onResultClick={handleResultClick}
          onResultShown={handleResultShown}
          snippetLinesShown={category.snippetLinesShown}
        />
      ) : (
        <CategoryImageGrid
          results={results}
          loading={loading}
          onResultClick={handleResultClick}
          onResultShown={handleResultShown}
          onSuccessfulLoad={onImageLoadSuccess}
        />
      )}
    </Box>
  );
}
